<mat-toolbar color="accent">Import from website</mat-toolbar>

<mat-dialog-content>
  @switch (phase()) {
    @case (phases.Idle) {
      <p class="mb-4 text-sm text-slate-500">
        We read your practice website and suggest values for the Knowledge Base.
        You choose what to use; nothing is saved until you save the editor.
      </p>

      <mat-form-field class="w-full" subscriptSizing="dynamic">
        <mat-label>Website address</mat-label>
        <input
          matInput
          id="import-website-url"
          type="url"
          autocomplete="url"
          aria-label="Website address"
          placeholder="e.g. https://www.example-dental.com"
          [formControl]="urlControl"
          (blur)="urlBlurred()"
        />
      </mat-form-field>

      @if (showUrlError()) {
        <p class="mt-1 text-sm text-red-600" role="alert">{{ urlError() }}</p>
      }

      @if (showPracticeSettingsHint()) {
        <p class="mt-1 text-sm text-slate-500">
          To remember this address, update your practice website in Practice
          Settings.
        </p>
      }

      @if (snapshotUsable()) {
        <mat-radio-group
          class="mt-4 flex flex-col gap-1"
          aria-label="Which read of the website to use"
          [value]="readingLastWebsiteRead()"
          (change)="useSnapshot.set($event.value)"
        >
          <mat-radio-button [value]="true">
            Use website read {{ snapshotReadAgo() }}
          </mat-radio-button>
          <mat-radio-button [value]="false">Fetch fresh</mat-radio-button>
        </mat-radio-group>
      }
    }

    @case (phases.Running) {
      <mat-progress-bar mode="indeterminate" class="mb-4" />
      <p aria-live="polite" class="text-sm text-slate-600">
        {{ runningMessage() }}
      </p>
    }

    @case (phases.Proposal) {
      <h3 class="mb-1 text-base font-medium text-slate-900">
        {{ proposalHeading() }}
      </h3>
      <p class="mb-4 text-sm text-slate-500">
        Tick the values you want to use. Anything you already have is shown
        beside the suggestion, and suggestions that would replace it, including
        those marked Update, start unticked. Nothing is saved until you save the
        editor.
      </p>

      @if (reviewGroups().length === 0) {
        <p class="text-sm text-slate-500">
          We read the website but found nothing to suggest.
        </p>
      }

      <div class="flex flex-col gap-6">
        @for (group of reviewGroups(); track group.sectionLabel) {
          <section>
            <h4 class="mb-2 text-sm font-semibold uppercase text-slate-500">
              {{ group.sectionLabel }}
            </h4>
            <div class="flex flex-col gap-3">
              @for (item of group.items; track item.id) {
                <div
                  class="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4"
                >
                  <mat-checkbox
                    class="mt-0.5"
                    [aria-label]="item.ariaLabel"
                    [checked]="item.selected"
                    (change)="toggleItem(item.id, $event.checked)"
                  />
                  <div class="flex min-w-0 flex-1 flex-col gap-2">
                    <div class="font-medium text-slate-900">
                      @if (item.categoryLabel) {
                        <span
                          class="mr-2 text-xs font-semibold uppercase text-slate-500"
                        >
                          {{ item.categoryLabel }}
                        </span>
                      }
                      @if (item.updateNote) {
                        <span
                          class="bg-primary-50 text-primary-500 mr-2 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                          data-testid="import-update-tag"
                        >
                          Update
                        </span>
                      }
                      {{ item.label }}
                    </div>

                    <div class="grid gap-3 sm:grid-cols-2">
                      <div class="flex flex-col gap-1">
                        <span
                          class="text-xs font-semibold uppercase text-slate-500"
                        >
                          Current
                        </span>
                        @if (item.current; as current) {
                          @if (current.heading) {
                            <span class="text-sm font-medium text-slate-700">
                              {{ current.heading }}
                            </span>
                          }
                          <span
                            class="whitespace-pre-line text-sm text-slate-700"
                          >
                            {{ current.body }}
                          </span>
                        } @else {
                          <span class="text-sm italic text-slate-400"
                            >Empty</span
                          >
                        }
                      </div>

                      <div class="flex flex-col gap-1">
                        <span
                          class="text-xs font-semibold uppercase text-slate-500"
                        >
                          Proposed
                        </span>
                        @if (item.proposed.heading) {
                          <span class="text-sm font-medium text-slate-900">
                            {{ item.proposed.heading }}
                          </span>
                        }
                        <span
                          class="whitespace-pre-line text-sm text-slate-900"
                        >
                          {{ item.proposed.body }}
                        </span>
                      </div>
                    </div>

                    @if (item.updateNote; as updateNote) {
                      <p class="text-xs text-slate-500">{{ updateNote }}</p>
                    }

                    <a
                      class="text-primary-600 self-start text-sm underline"
                      target="_blank"
                      rel="noopener noreferrer"
                      [href]="item.provenance"
                      >Source page</a
                    >
                  </div>
                </div>
              }
            </div>
          </section>
        }
      </div>
    }

    @case (phases.Empty) {
      <p class="text-sm text-slate-700">{{ emptyMessage() }}</p>
    }

    @case (phases.Error) {
      <p class="text-sm text-red-600" role="alert">{{ errorMessage() }}</p>
    }
  }
</mat-dialog-content>

<mat-dialog-actions>
  <pt-buttons-container padding="none">
    @switch (phase()) {
      @case (phases.Idle) {
        <button mat-button mat-dialog-close type="button">Cancel</button>
        <button
          mat-flat-button
          color="primary"
          type="button"
          [disabled]="canImport() === false"
          (click)="runImport()"
        >
          Import
        </button>
      }
      @case (phases.Running) {
        <button mat-button type="button" disabled>Cancel</button>
        <button mat-flat-button color="primary" type="button" disabled>
          Import
        </button>
      }
      @case (phases.Proposal) {
        <button mat-button mat-dialog-close type="button">Cancel</button>
        <button
          mat-flat-button
          color="primary"
          type="button"
          [disabled]="selectedCount() === 0"
          (click)="applySelected()"
        >
          Apply selected
        </button>
      }
      @case (phases.Empty) {
        <button mat-button mat-dialog-close type="button">Cancel</button>
        <button
          mat-flat-button
          color="primary"
          type="button"
          (click)="tryAgain()"
        >
          Try again
        </button>
      }
      @case (phases.Error) {
        <button mat-button mat-dialog-close type="button">
          {{ canRetry() ? 'Cancel' : 'Close' }}
        </button>
        @if (canRetry()) {
          <button
            mat-flat-button
            color="primary"
            type="button"
            (click)="tryAgain()"
          >
            Try again
          </button>
        }
      }
    }
  </pt-buttons-container>
</mat-dialog-actions>
